import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";

const PageTitleContext = createContext(null);

const PageTitleProvider = ({ children, defaultTitle = "Student Hub" }) => {
    const [title, setTitle] = useState(defaultTitle);

    const setPageTitle = useCallback(
        (nextTitle) => {
            setTitle(nextTitle || defaultTitle);
        },
        [defaultTitle],
    );

    const value = useMemo(
        () => ({ title, setPageTitle, defaultTitle }),
        [title, setPageTitle, defaultTitle],
    );

    return (
        <PageTitleContext.Provider value={value}>
            {children}
        </PageTitleContext.Provider>
    );
};

const usePageTitleContext = () => {
    const context = useContext(PageTitleContext);
    if (!context) {
        throw new Error(
            "usePageTitleContext must be used within PageTitleProvider",
        );
    }

    return context;
};

const usePageTitle = (nextTitle) => {
    const { setPageTitle, defaultTitle } = usePageTitleContext();

    useEffect(() => {
        if (!nextTitle) {
            return undefined;
        }

        setPageTitle(nextTitle);

        return () => {
            setPageTitle(defaultTitle);
        };
    }, [nextTitle, setPageTitle, defaultTitle]);

    return { setPageTitle };
};

export { PageTitleProvider, usePageTitleContext, usePageTitle };
